import React, {useEffect, useRef} from 'react';
import {Notification} from '../navigation/page-layout';
import {RegionId, ServiceResource, useAuth} from '../hooks/use-auth';
import {translateErrorToReactNode} from '../common';
import PortalLoader from '../common/PortalLoader';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import '../../assets/styles/forecast-styles.scss';
import {AppLayout, ContentLayout} from '@amzn/awsui-components-react';
import {useGetDashboardUrlByDashboardName} from '../hooks/use-quicksight-api';

export function ViewOteAnomalies(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const selectedRegion = RegionId.EU;

    const clientConfigs = auth.authInformation!.getEndpoints()[selectedRegion][ServiceResource.ForecastStoreInsights];

    function createErrorListener(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {status: getDashboardUrlStatus, value: getDashboardUrlResponse} = useGetDashboardUrlByDashboardName(
        clientConfigs,
        {dashboardName: 'OTE Anomaly Detection'},
        true,
        createErrorListener('GetDashboardUrlByDashboardName failed'),
        [auth, selectedRegion]
    );

    const isLoading = getDashboardUrlStatus === 'idle' || getDashboardUrlStatus === 'pending';

    return (
        <AppLayout
            disableContentPaddings={true}
            content={
                <ContentLayout>
                    {isLoading ? (
                        <PortalLoader />
                    ) : (
                        <OteAnomalyDashboard
                            key={selectedRegion}
                            url={getDashboardUrlResponse?.dashboardUrl || ''}
                            pushNotification={props.pushNotification}
                        />
                    )}
                </ContentLayout>
            }
            toolsHide
            navigationHide
        />
    );
}

interface OteAnomalyDashboardProps {
    url: string;
    pushNotification: (notification: Notification) => void;
}

function OteAnomalyDashboard(props: OteAnomalyDashboardProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    function onError(error: any) {
        props.pushNotification({
            type: 'error',
            header: 'Embedding Quicksight Dashboard failed',
            content: translateErrorToReactNode(error),
        });
    }

    useEffect(() => {
        if (!ref.current || !props.url) return;
        const dashboard = QuickSightEmbedding.embedDashboard({
            url: props.url,
            container: ref.current,
            locale: 'en-US',
            footerPaddingEnabled: true,
            parameters: {},
        });
        dashboard.on('error', onError);
    }, []);

    return <div id="embeddingContainer" ref={ref} />;
}
